/**
 * The insights engine. Pure on purpose: every product rule from the spec's
 * decision table lives here, where it can be tested without a database, a
 * network, or a clock. computeInsights(rows + dates) -> report; the page only
 * renders what this returns.
 */
import {
  ACTIVE_WINDOW_DAYS, BENCHMARK_MIN_ADS, BENCHMARK_MIN_IMPRESSIONS,
  MIN_CLICKS_30D, MIN_IMPRESSIONS_30D,
  RANK_KEY_BY_OBJECTIVE, type RankKey,
  MAX_PAIRS_PER_POOL, MIN_CONVERSIONS_30D, MIN_NEW_AD_IMPRESSIONS, MIN_POOL_SIZE,
  MIN_SHIFT_PER_DAY, MIN_SPEND_30D, MOVER_MIN_SPEND_7D, MOVER_THRESHOLD, UNRANKED_MIN_SPEND,
  REALLOC_STEP, STALE_AFTER_DAYS, ESTABLISHED_AFTER_DAYS,
} from "./insightConstants";
import { isFixedRate } from "./adEfficiencyRow";
import type {
  AccumulatingGroup, AdEfficiencyRow, Freshness, InsightInput, InsightReport,
  CreativeQualityBlock, CreativeQualityRow, CreativeQualityVerdict,
  FixedRateGroup, Mover, NewAdBlock, NewAdRow, NewAdVerdict, PlatformSummary, PlatformTotalRow, Pool, RankedAd,
  Recommendation, StoppedAd, UnmeasurableGroup,
} from "./insightTypes";

const MS_PER_DAY = 86_400_000;

/** Whole days from `fromISO` to `toISO` (negative when `toISO` is earlier). */
export const daysBetween = (fromISO: string, toISO: string): number =>
  Math.floor((Date.parse(toISO) - Date.parse(fromISO)) / MS_PER_DAY);

const freshnessOf = (latestDataDate: string | null, todayISO: string): Freshness => {
  // Unknown horizon reads as stale: "we can't tell how old this is" must never
  // render as "fresh".
  if (!latestDataDate) return { latestDataDate: null, ageDays: null, stale: true };
  const ageDays = daysBetween(latestDataDate, todayISO);
  return { latestDataDate, ageDays, stale: ageDays > STALE_AFTER_DAYS };
};

/** D4 — every condition disqualifies independently. */
const isEligible = (r: AdEfficiencyRow, todayISO: string): boolean =>
  r.status === "active" &&
  r.lastSpendDate !== null &&
  daysBetween(r.lastSpendDate, todayISO) < ACTIVE_WINDOW_DAYS &&
  r.spend >= MIN_SPEND_30D;
  // The volume floor is per rank key (conversions/clicks/impressions) and
  // lives beside the pool that applies it.

/** Standard median; average of the two middles for even counts. */
export const median = (sortedAsc: number[]): number => {
  const mid = Math.floor(sortedAsc.length / 2);
  return sortedAsc.length % 2 ? sortedAsc[mid] : (sortedAsc[mid - 1] + sortedAsc[mid]) / 2;
};

// ---------------------------------------------------------------------------
// New-ads block
// ---------------------------------------------------------------------------

/** Percentile of a sorted-ascending array, linear interpolation. */
const percentile = (sortedAsc: number[], q: number): number => {
  if (sortedAsc.length === 0) return 0;
  const pos = (sortedAsc.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return lo === hi ? sortedAsc[lo] : sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * (pos - lo);
};

/**
 * 95% interval on a click-through rate.
 *
 * Binomial rather than Poisson on purpose: clicks and impressions are integer
 * counts on every platform, whereas Google reports weighted, FRACTIONAL
 * conversions (70 of 295 rows on the audited workspace). Interval machinery on
 * those would be a category error, which is why nothing here touches them.
 */
const ctrInterval = (clicks: number, impressions: number): { low: number; high: number } => {
  if (impressions <= 0) return { low: 0, high: 0 };
  const p = clicks / impressions;
  const se = Math.sqrt(Math.max(p * (1 - p), 0) / impressions);
  return { low: Math.max(0, p - 1.96 * se) * 100, high: Math.min(1, p + 1.96 * se) * 100 };
};

/** Whole days from `from` to `to`, inclusive of the first day. */
export const ageInDays = (from: string, todayISO: string): number =>
  Math.max(1, daysBetween(from, todayISO) + 1);

/**
 * Judge the ads that launched recently, against the pool each belongs to.
 *
 * Pool-relative because a single threshold cannot serve pools whose CTR medians
 * differ by an order of magnitude (facebook·consideration 2.06% against
 * awareness 0.235% on the audited workspace). A low-CTR pool simply lands its
 * new ads in "not enough evidence yet" for longer, which is the honest outcome
 * rather than a special case.
 */
export const computeNewAds = (
  rows: AdEfficiencyRow[],
  todayISO: string,
  ageDays: number,
): NewAdBlock => {
  const key = (r: AdEfficiencyRow): string => `${r.platform}|${r.objective ?? "unspecified"}`;

  // Benchmarks come from ads with enough delivery to have a stable rate. A new
  // ad may sit in its own benchmark; with BENCHMARK_MIN_IMPRESSIONS at 500 that
  // is rare and its effect on a percentile is slight.
  const benchmarks = new Map<string, { p25: number; median: number; p75: number; n: number }>();
  const byPool = new Map<string, number[]>();
  for (const r of rows) {
    // A reservation's CTR is a property of its placement, not its creative —
    // it neither benchmarks a pool nor gets judged against one.
    if (isFixedRate(r)) continue;
    if (r.impressions < BENCHMARK_MIN_IMPRESSIONS) continue;
    const arr = byPool.get(key(r)) ?? [];
    arr.push((r.clicks / r.impressions) * 100);
    byPool.set(key(r), arr);
  }
  for (const [k, arr] of byPool) {
    if (arr.length < BENCHMARK_MIN_ADS) continue;
    const sorted = [...arr].sort((a, b) => a - b);
    benchmarks.set(k, {
      p25: percentile(sorted, 0.25),
      median: percentile(sorted, 0.5),
      p75: percentile(sorted, 0.75),
      n: sorted.length,
    });
  }

  const ads: NewAdRow[] = [];
  for (const r of rows) {
    if (isFixedRate(r)) continue;
    if (!r.firstSpendDate) continue;
    const age = ageInDays(r.firstSpendDate, todayISO);
    if (age > ageDays) continue;

    const bench = benchmarks.get(key(r)) ?? null;
    const ctr = r.impressions > 0 ? (r.clicks / r.impressions) * 100 : 0;
    const ci = ctrInterval(r.clicks, r.impressions);

    let verdict: NewAdVerdict;
    if (r.impressions < MIN_NEW_AD_IMPRESSIONS) verdict = "tooEarly";
    else if (!bench) verdict = "noBenchmark";
    else if (ci.high < bench.p25) verdict = "belowPool";
    else if (ci.low > bench.p75) verdict = "abovePool";
    else verdict = "inconclusive";

    // Impressions at which the half-width would shrink enough to clear the
    // nearer pool edge. SE scales as 1/sqrt(n), so the requirement scales as
    // the square of how much the interval must tighten.
    let impressionsNeeded = 0;
    if (verdict === "inconclusive" && bench) {
      const p = ctr / 100;
      const edge = ctr < bench.median ? bench.p25 : bench.p75;
      const gap = Math.abs(ctr - edge) / 100;
      if (gap > 0) {
        const needed = (1.96 ** 2 * Math.max(p * (1 - p), 1e-9)) / gap ** 2;
        impressionsNeeded = Math.max(0, Math.ceil(needed - r.impressions));
      }
    }

    ads.push({
      adId: r.adId, adName: r.adName, platform: r.platform,
      objective: r.objective ?? "unspecified",
      ageDays: age,
      impressions: r.impressions, clicks: r.clicks, spend: r.spend,
      conversions: r.conversions,
      ctr, ctrCiLowPct: ci.low, ctrCiHighPct: ci.high,
      poolCtrP25Pct: bench?.p25 ?? null,
      poolCtrMedianPct: bench?.median ?? null,
      poolCtrP75Pct: bench?.p75 ?? null,
      verdict, impressionsNeeded,
    });
  }

  // Worst first: a decision to make beats a decision already going well.
  const order: Record<NewAdVerdict, number> = {
    belowPool: 0, abovePool: 1, inconclusive: 2, tooEarly: 3, noBenchmark: 4,
  };
  ads.sort((a, b) => order[a.verdict] - order[b.verdict] || b.spend - a.spend);

  // What the median new ad's evidence can actually resolve, as a share of its
  // pool median — the age picker's label, measured rather than asserted.
  const resolvable = ads
    .filter((a) => a.poolCtrMedianPct && a.poolCtrMedianPct > 0 && a.impressions > 0)
    .map((a) => ((a.ctrCiHighPct - a.ctrCiLowPct) / 2 / (a.poolCtrMedianPct as number)) * 100)
    .sort((x, y) => x - y);
  const resolvableGapPct = resolvable.length ? percentile(resolvable, 0.5) : 0;

  return { ads, ageDays, resolvableGapPct };
};



/**
 * Creative quality for ESTABLISHED ads — the census half of the creative
 * question. Same verdict machinery as computeNewAds (binomial CI on CTR
 * against the ad's OWN pool percentiles), applied to every active auction ad
 * OLDER than the widest new-ads window, so the two blocks partition ad age
 * and no ad is judged twice on one screen.
 *
 * This is what gives a platform with no conversion tracking (LINE: zero
 * conversions on every row while spending ฿118k/30d) a fair creative
 * comparison — clicks and impressions are integers on every platform, so the
 * interval is safe where conversion machinery would not be. Direction-only by
 * design: an ad holds no budget column (media_ads has none), so a verdict
 * here suggests reviewing the creative, never moving money.
 */
export const computeCreativeQuality = (
  rows: AdEfficiencyRow[],
  todayISO: string,
  /**
   * Ads this age or younger belong to the NEW-ADS block. Pass the age picker's
   * current value so the two blocks always partition: with a fixed constant
   * here and the picker at 3 days, ads aged 4-14 days were judged by neither
   * (48 active ads, ฿98,199, while the header said "all running ads").
   */
  newAdWindowDays: number = ESTABLISHED_AFTER_DAYS,
): CreativeQualityBlock => {
  const key = (r: AdEfficiencyRow): string => `${r.platform}|${r.objective ?? "unspecified"}`;

  // Benchmarks mirror computeNewAds exactly — the same pool percentiles judge
  // both a day-2 ad and a month-old one, or the two blocks would disagree
  // about where the pool stands.
  const byPool = new Map<string, number[]>();
  for (const r of rows) {
    if (isFixedRate(r)) continue;
    if (r.impressions < BENCHMARK_MIN_IMPRESSIONS) continue;
    const arr = byPool.get(key(r)) ?? [];
    arr.push((r.clicks / r.impressions) * 100);
    byPool.set(key(r), arr);
  }
  const benchmarks = new Map<string, { p25: number; median: number; p75: number }>();
  for (const [k, arr] of byPool) {
    if (arr.length < BENCHMARK_MIN_ADS) continue;
    const sorted = [...arr].sort((a, b) => a - b);
    benchmarks.set(k, {
      p25: percentile(sorted, 0.25),
      median: percentile(sorted, 0.5),
      p75: percentile(sorted, 0.75),
    });
  }

  const ads: CreativeQualityRow[] = [];
  for (const r of rows) {
    // A paused ad is a decision already taken; a reservation's CTR is its
    // placement, not its creative. Below the impressions floor the interval
    // is wider than any pool gap — nothing honest to say yet.
    if (r.status !== "active") continue;
    if (isFixedRate(r)) continue;
    if (r.impressions < BENCHMARK_MIN_IMPRESSIONS) continue;
    // Age ≤ the picker's window belongs to the new-ads block. A row with no
    // firstSpendDate predates the backfill — certainly established.
    if (r.firstSpendDate && ageInDays(r.firstSpendDate, todayISO) <= newAdWindowDays) continue;

    const bench = benchmarks.get(key(r)) ?? null;
    const ctr = (r.clicks / r.impressions) * 100;
    const ci = ctrInterval(r.clicks, r.impressions);
    const verdict: CreativeQualityVerdict = !bench
      ? "noBenchmark"
      : ci.high < bench.p25
        ? "belowPool"
        : ci.low > bench.p75
          ? "abovePool"
          : "inconclusive";

    ads.push({
      adId: r.adId, adName: r.adName, platform: r.platform,
      objective: r.objective ?? "unspecified",
      impressions: r.impressions, clicks: r.clicks, spend: r.spend,
      ctr,
      cpc: r.clicks > 0 ? r.spend / r.clicks : null,
      poolCtrP25Pct: bench?.p25 ?? null,
      poolCtrMedianPct: bench?.median ?? null,
      poolCtrP75Pct: bench?.p75 ?? null,
      // The one quality figure comparable ACROSS pools: a raw CTR gap means
      // different things in a 0.2%-median pool and a 2%-median pool, but
      // "0.4× of its own pool" means the same thing in both.
      ctrVsMedian: bench && bench.median > 0 ? ctr / bench.median : null,
      verdict,
    });
  }

  // Quality order, best→worst: descending ratio, so the screen reads from the
  // strongest creative down to the money at risk. No benchmark (no ratio)
  // sorts last; ties break toward the bigger spender.
  ads.sort(
    (a, b) =>
      (b.ctrVsMedian ?? Number.NEGATIVE_INFINITY) - (a.ctrVsMedian ?? Number.NEGATIVE_INFINITY) ||
      b.spend - a.spend,
  );

  return { ads };
};

export const computeInsights = (input: InsightInput): InsightReport => {
  const { rows, todayISO, latestDataDate, platformTotals } = input;
  const freshness = freshnessOf(latestDataDate, todayISO);

  // Stage 3: the RANKING half runs at ad-set grain when the caller provides it
  // — the ad set is the unit that holds a budget, and it reaches a decidable
  // conversion count ~3x faster than an ad. The creative-level half (new ads,
  // movers, stopped) always stays on the ad rows.
  const unitRows = input.adSetRows ?? rows;

  // --- fixed-price reservations leave the flow FIRST (stage 2): ranking one
  // on cost efficiency would judge a price agreed in advance, and folding one
  // into "unmeasurable" would call a booked rate a tracking defect. The
  // verdict is the row's `fixedRate` stamp, decided once in mapEfficiencyRows
  // (own complete-day CPM spread, or inherited from the ad set); nothing here
  // re-derives it.
  const fixedRateMap = new Map<string, { platform: string; objective: string; ads: AdEfficiencyRow[] }>();
  const auctionRows: AdEfficiencyRow[] = [];
  for (const r of unitRows) {
    if (isFixedRate(r)) {
      const objective = r.objective ?? "unknown";
      const key = `${r.platform}|${objective}`;
      const g = fixedRateMap.get(key) ?? { platform: r.platform, objective, ads: [] };
      g.ads.push(r);
      fixedRateMap.set(key, g);
    } else {
      auctionRows.push(r);
    }
  }
  const fixedRate: FixedRateGroup[] = [...fixedRateMap.values()].map((g) => {
    // One shared rate is stated; mixed rates are disclosed without inventing one.
    // Only units with impressions can state a rate: an ad stamped from its ad
    // set's verdict may carry cost alone in the window (the "self" rule needs
    // impressions, the inherited one does not), and dividing by its zero
    // impressions would put Infinity/NaN into the set — reading as "mixed" or
    // worse. Such a unit stays in the group (the stamp decides membership) but
    // contributes no rate; if no unit has one, cpmRate is null like a mixed
    // group, which the renderer already handles.
    const rates = [...new Set(
      g.ads.filter((a) => a.impressions > 0)
        .map((a) => Math.round((a.spend / a.impressions) * 1000 * 100) / 100),
    )];
    return {
      platform: g.platform,
      objective: g.objective,
      adCount: g.ads.length,
      spend: g.ads.reduce((sum, a) => sum + a.spend, 0),
      cpmRate: rates.length === 1 ? rates[0] : null,
    };
  });

  // --- group by platform x objective (D2). A null objective groups under
  // "unknown" rather than being dropped: its spend is still real money.
  const groups = new Map<string, AdEfficiencyRow[]>();
  for (const r of auctionRows) {
    const objectiveKey = r.objective ?? "unknown";
    const key = `${r.platform}|${objectiveKey}`;
    const list = groups.get(key);
    if (list) list.push(r); else groups.set(key, [r]);
  }

  const pools: Pool[] = [];
  const unmeasurable: UnmeasurableGroup[] = [];
  const accumulating: AccumulatingGroup[] = [];

  for (const [key, groupRows] of groups) {
    const [platform, objectiveStr] = key.split("|");
    const objective = objectiveStr;
    const groupSpend = groupRows.reduce((s, r) => s + r.spend, 0);
    const groupConv = groupRows.reduce((s, r) => s + r.conversions, 0);
    // Each pool ranks on the goal its campaigns were bought against (stage 4).
    const rankKey: RankKey = RANK_KEY_BY_OBJECTIVE[objective] ?? "cpa";

    // D7: real money, zero measurement — but ONLY where the pool's goal IS
    // conversions. A consideration pool with zero conversions is CPC-bought
    // traffic doing its job, not a tracking defect; ranking it on CPA was how
    // ฿117,715 of LINE spend got labelled "unmeasurable".
    if (rankKey === "cpa" && groupConv === 0) {
      if (groupSpend >= MIN_SPEND_30D) {
        unmeasurable.push({ platform, objective, spend: groupSpend, adCount: groupRows.length });
      }
      continue;
    }

    const metricValue = (r: AdEfficiencyRow): number | null => {
      if (rankKey === "cpa") return r.conversions > 0 ? r.spend / r.conversions : null;
      if (rankKey === "cpc") return r.clicks > 0 ? r.spend / r.clicks : null;
      return r.impressions > 0 ? (r.spend / r.impressions) * 1000 : null;
    };
    const meetsVolumeFloor = (r: AdEfficiencyRow): boolean => {
      if (rankKey === "cpa") return r.conversions >= MIN_CONVERSIONS_30D;
      if (rankKey === "cpc") return r.clicks >= MIN_CLICKS_30D;
      return r.impressions >= MIN_IMPRESSIONS_30D;
    };

    const ranked: RankedAd[] = groupRows
      .filter((r) => isEligible(r, todayISO) && meetsVolumeFloor(r) && metricValue(r) !== null)
      .map((r) => ({
        adId: r.adId, adName: r.adName, platform, objective,
        rankKey,
        value: metricValue(r) as number,
        // Completed days only: today is a partial row until it is over, and
        // dividing a third of a day's money by a whole day understates the rate.
        spendPerDay: r.spendCompleted / Math.max(r.daysWithSpendCompleted, 1),
        spend: r.spend, conversions: r.conversions,
      }))
      .sort((a, b) => a.value - b.value);

    if (ranked.length === 0) {
      // Converting money with no rankable ad yet — new ads still ramping.
      // Silence here was final-review Finding 5: 466k of spend rendered as
      // nothing. Only ACTIVE ads count: "accumulating" is a claim about ads
      // still gathering data, and the first live render said "394 new ads"
      // when most were the paused/expired old generation. A group with no
      // active ad accumulates nothing and stays out.
      const activeCount = groupRows.filter((r) => r.status === "active").length;
      if (activeCount > 0) {
        accumulating.push({ platform, objective, adCount: activeCount, spend: groupSpend });
      }
      continue;
    }

    const med = median(ranked.map((r) => r.value));
    const tooSmall = ranked.length < MIN_POOL_SIZE;
    pools.push({
      platform, objective, rankKey, ranked, medianValue: med,
      recommendations:
        tooSmall || freshness.stale ? [] : pairRecommendations(ranked, med),
      tooSmall,
    });
  }

  return {
    freshness,
    adCount: unitRows.length,
    // The strip keeps ALL unit rows: a reservation's money is real spend, and
    // excluding it would resurrect a phantom unrankedSpend gap. At ad-set
    // grain this level equals the platform totals, so the PMax disclosure
    // naturally collapses to zero.
    crossPlatform: crossPlatformSummary(unitRows, todayISO, platformTotals),
    pools,
    unmeasurable,
    accumulating,
    fixedRate,
    // Movers and the stopped list are CREATIVE-level observations: always the
    // ad rows, minus reservations — a booking running out is not an ad
    // stopping.
    movers: findMovers(rows.filter((r) => !isFixedRate(r))),
    stopped: findStopped(rows.filter((r) => !isFixedRate(r))),
    recommendationsSuppressed: freshness.stale,
  };
};

/** D6. `ranked` is CPA-ascending. Pair the k-th best with the k-th worst,
 *  capped at MAX_PAIRS_PER_POOL, each strictly on its own side of the median;
 *  amount = REALLOC_STEP of the DONOR's spend/day, skipped when below
 *  MIN_SHIFT_PER_DAY. Each increase mirrors its decrease exactly, so every
 *  pool balances by construction. */
const pairRecommendations = (ranked: RankedAd[], medianValue: number): Recommendation[] => {
  const recs: Recommendation[] = [];
  const pairs = Math.min(MAX_PAIRS_PER_POOL, Math.floor(ranked.length / 2));
  for (let k = 0; k < pairs; k++) {
    const receiver = ranked[k];
    const donor = ranked[ranked.length - 1 - k];
    if (!(receiver.value < medianValue && donor.value > medianValue)) break;
    const amountPerDay = donor.spendPerDay * REALLOC_STEP;
    if (amountPerDay < MIN_SHIFT_PER_DAY) continue;
    recs.push({ action: "decrease", ad: donor, groupMedianValue: medianValue, amountPerDay, pairedAdId: receiver.adId });
    recs.push({ action: "increase", ad: receiver, groupMedianValue: medianValue, amountPerDay, pairedAdId: donor.adId });
  }
  // Increases first: the page leads with what to grow.
  return recs.sort((a, b) => (a.action === b.action ? 0 : a.action === "increase" ? -1 : 1));
};

/** D9 movers. Spend floors on BOTH windows; conversions collapsing to zero is
 *  a "worse" mover with change null (CPA -> infinity has no percentage). */
const findMovers = (rows: AdEfficiencyRow[]): Mover[] => {
  const movers: Mover[] = [];
  for (const r of rows) {
    // Running ads only (user decision 2026-09-02): a paused or expired ad's
    // CPA swing is noise — there is nothing left to act on.
    if (r.status !== "active") continue;
    if (r.spendRecent7 < MOVER_MIN_SPEND_7D || r.spendPrior7 < MOVER_MIN_SPEND_7D) continue;
    if (r.convPrior7 <= 0) continue; // no baseline to compare against
    const cpaPrior = r.spendPrior7 / r.convPrior7;
    if (r.convRecent7 <= 0) {
      movers.push({ adId: r.adId, adName: r.adName, platform: r.platform,
        cpaRecent: null, cpaPrior, change: null, direction: "worse" });
      continue;
    }
    const cpaRecent = r.spendRecent7 / r.convRecent7;
    const change = (cpaRecent - cpaPrior) / cpaPrior;
    if (Math.abs(change) < MOVER_THRESHOLD) continue;
    movers.push({ adId: r.adId, adName: r.adName, platform: r.platform,
      cpaRecent, cpaPrior, change, direction: change > 0 ? "worse" : "better" });
  }
  return movers;
};

/** D9, narrowed 2026-09-02 (user decision): status ACTIVE but the money
 *  stopped flowing — an anomaly worth a look (budget exhausted, platform
 *  halt). A deliberately paused or expired ad is not an alert, so the
 *  original "whatever the console status says" reading was retired. */
const findStopped = (rows: AdEfficiencyRow[]): StoppedAd[] =>
  rows
    .filter((r) => r.status === "active" && r.spendRecent7 === 0 && r.spendPrior7 > 0)
    .map((r) => ({ adId: r.adId, adName: r.adName, platform: r.platform, lastSpendDate: r.lastSpendDate }));

/** D10 — labeled coarse in the UI; conversion definitions differ per platform.
 *  With adset-level totals (audit F1) those are the truth for spend/CPA/share —
 *  PMax-shaped ad sets carry spend that no ad row sees, 25,230 THB (22.6% of
 *  google) at audit time. Ad rows still measure what the RANKING can see, and
 *  the difference is disclosed as unrankedSpend rather than hidden. The totals
 *  also carry each platform's own newest data date (migration 085), aged here
 *  with the same arithmetic as the page-level freshness. */
const crossPlatformSummary = (
  rows: AdEfficiencyRow[],
  todayISO: string,
  platformTotals?: PlatformTotalRow[],
): PlatformSummary[] => {
  const adLevel = new Map<string, { spend: number; conversions: number; latestDataDate: string | null }>();
  for (const r of rows) {
    const acc = adLevel.get(r.platform) ?? { spend: 0, conversions: 0, latestDataDate: null };
    acc.spend += r.spend;
    acc.conversions += r.conversions;
    adLevel.set(r.platform, acc);
  }

  // An EMPTY totals array degrades to ad-level rather than blanking the strip
  // (a failed or empty adset read must never erase figures the ads can show).
  // On that fallback there is no per-platform date, and none is invented.
  const useTotals = platformTotals !== undefined && platformTotals.length > 0;
  const source = useTotals
    ? new Map(platformTotals!.map((t) => [t.platform, { spend: t.spend, conversions: t.conversions, latestDataDate: t.latestDataDate }]))
    : adLevel;

  const total = [...source.values()].reduce((s, p) => s + p.spend, 0);
  return [...source.entries()]
    .map(([platform, p]) => ({
      platform, spend: p.spend, conversions: p.conversions,
      blendedCpa: p.conversions > 0 ? p.spend / p.conversions : null,
      spendShare: total > 0 ? p.spend / total : 0,
      unrankedSpend: useTotals
        ? (() => {
            const gap = p.spend - (adLevel.get(platform)?.spend ?? 0);
            return gap >= UNRANKED_MIN_SPEND ? gap : 0;
          })()
        : 0,
      latestDataDate: p.latestDataDate,
      dataAgeDays: p.latestDataDate ? daysBetween(p.latestDataDate, todayISO) : null,
    }))
    .sort((a, b) => b.spend - a.spend);
};
